// File: frontend/src/components/marketing/visuals/PartnerTierBadgeVisual.js
//
// The five PesaFii Ambassador tier medallions, as inline SVG.
//
// ── PUBLIC API (other surfaces import this; keep it stable) ────────────────
//
//   <TierBadgeArt tier="gold" size={84} label="Gold tier" />
//
//   tier      bronze | silver | gold | platinum | diamond (unknown → bronze)
//   size      px number or an MUI breakpoint object ({ xs: 48, md: 84 })
//   label     accessible name; null/undefined renders it aria-hidden
//   selected  optional: a soft brand-coloured halo for a chosen medallion
//   sx        optional: extra sx merged onto the <svg>
//
// ── WHY INLINE SVG AND NOT FILES ────────────────────────────────────────────
//
// Five <img> files would be five requests and five FIXED colour schemes. Inline
// means the metal is chosen at render time from the active theme mode. All five
// together stay a few KB gzipped. No bitmaps, no filters inside the SVG.
//
// ── WHY IT LIVES IN visuals/ ────────────────────────────────────────────────
//
// package.json's eslintConfig bans raw hex under src/pages/** and
// src/components/**, and exempts src/components/marketing/visuals/**, the home
// for hand-drawn marketing art that carries its own palette. A bronze medallion
// is not a status colour; there is no palette token for "bronze metal", and
// inventing one would push a decorative hue into the token set every dashboard
// reads. The discipline that matters is kept: every metal has a light-mode and
// a dark-mode variant, chosen from theme.palette.mode.
//
// ── HOW THE TIERS ARE TOLD APART (colour AND shape) ────────────────────────
//
//   Bronze    warm copper · single rim · one chevron
//   Silver    neutral cool grey · single rim · fine inner ring · two chevrons
//   Gold      rich warm gold · rim studded at the six corners · three chevrons
//   Platinum  pearl/ice with a blue-violet iridescent field · a DETACHED outer
//             rim (double rim) · a four-point star, no chevrons at all
//   Diamond   cyan crystal · faceted field cut into six planes · brilliant-cut
//             gemstone emblem with a sparkle
//
// Platinum used to be "Silver with more chevrons" and a reader could not tell
// them apart at 48px. It now differs from Silver on four axes: hue (lavender
// ice against neutral grey), field (iridescent against flat), silhouette (two
// rims against one) and emblem (a star against chevrons). The tier name is
// still always rendered as text beside any badge that carries meaning.
//
// ── THE GRADIENT-ID TRAP ────────────────────────────────────────────────────
//
// SVG <defs> ids are document-global. Five badges each defining id="metal"
// would all paint with whichever rendered last, and it only breaks once more
// than one badge is on screen. useId() makes every instance's ids unique.

import React, { useId } from "react";
import { Box, useTheme } from "@mui/material";

// ── THE METALS ──────────────────────────────────────────────────────────────
//
// hi / sheen are the specular lights, mid the body, lo the shadowed edge, ink
// the engraving shadow. Dark variants lift the floor: a metal on a near-black
// ground needs a compressed range or its lower half disappears into the page.
const METALS = {
  bronze: {
    light: { hi: "#F6CFA6", sheen: "#DC9E69", mid: "#B8743F", lo: "#74401F", ink: "#3F2210" },
    dark:  { hi: "#FAD8B4", sheen: "#E2A673", mid: "#C3814C", lo: "#84502C", ink: "#2A160A" },
  },
  silver: {
    light: { hi: "#FFFFFF", sheen: "#E4E7EB", mid: "#B8BDC4", lo: "#767C85", ink: "#3A3F46" },
    dark:  { hi: "#FFFFFF", sheen: "#E9ECEF", mid: "#C2C7CE", lo: "#868C95", ink: "#1F2328" },
  },
  gold: {
    light: { hi: "#FFF2BF", sheen: "#F7D163", mid: "#DBA321", lo: "#8C5F07", ink: "#453003" },
    dark:  { hi: "#FFF5CC", sheen: "#F9D86E", mid: "#E4AF2E", lo: "#9C6D12", ink: "#2B1E02" },
  },
  platinum: {
    // Pearl and ice with a blue-violet undertone; the iri* stops paint the
    // iridescent field that Silver does not have.
    light: {
      hi: "#FFFFFF", sheen: "#ECE9FB", mid: "#C3C9E9", lo: "#7E88B6", ink: "#2E3457",
      iri1: "#DCEBFF", iri2: "#EADCFB", iri3: "#DDF6F3",
    },
    dark: {
      hi: "#FFFFFF", sheen: "#F0EDFD", mid: "#CDD3F0", lo: "#8E98C4", ink: "#1C2140",
      iri1: "#E3EFFF", iri2: "#EFE3FD", iri3: "#E4F9F6",
    },
  },
  diamond: {
    light: { hi: "#F2FFFF", sheen: "#AEF1FF", mid: "#4ECFEA", lo: "#137E9F", ink: "#063646", deep: "#0A5C78" },
    dark:  { hi: "#F6FFFF", sheen: "#BDF5FF", mid: "#62DAF1", lo: "#2394B6", ink: "#042A37", deep: "#0E6D8C" },
  },
};

const CHEVRONS = { bronze: 1, silver: 2, gold: 3 };

// ── GEOMETRY ────────────────────────────────────────────────────────────────
//
// A pointed-top hexagon centred on (48, 54) in a 96 × 108 box. The unit shape
// is the historical PLATE outline, so the badge keeps its old footprint and
// every existing caller keeps its layout.
const CX = 48;
const CY = 54;
const UNIT = [[0, -49], [41, -25], [41, 25], [0, 49], [-41, 25], [-41, -25]];
const hex = (s) => UNIT.map(([x, y]) => [CX + x * s, CY + y * s]);
const pts = (list) => list.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(" ");

/** Chevron stack, centred on the field. */
function chevronPaths(count) {
  const gap = 11.5;
  return Array.from({ length: count }, (_, i) => {
    const top = CY - ((count - 1) * gap) / 2 + i * gap;
    return `M34 ${top + 5} L48 ${top - 4} L62 ${top + 5}`;
  });
}

// Four-point star for Platinum.
const STAR = "M48 35 L51.6 50.4 L66 54 L51.6 57.6 L48 73 L44.4 57.6 L30 54 L44.4 50.4 Z";
// Brilliant-cut gem for Diamond: table, girdle, pavilion.
const GEM_OUTLINE = "M37 44 L42 37 L54 37 L59 44 L48 72 Z";
const GEM_LINES = ["M37 44 L59 44", "M42 37 L45 44 L48 72", "M54 37 L51 44 L48 72", "M45 44 L48 37 L51 44"];

export default function TierBadgeArt({ tier, size = 88, label, selected = false, sx }) {
  const theme = useTheme();
  const raw = useId();
  const uid = `tb${raw.replace(/[^a-zA-Z0-9_-]/g, "")}`;

  const key = METALS[tier] ? tier : "bronze";
  const dark = theme.palette.mode === "dark";
  const m = METALS[key][dark ? "dark" : "light"];

  const id = (name) => `${uid}-${name}`;
  const url = (name) => `url(#${id(name)})`;

  const isPlatinum = key === "platinum";
  const isDiamond = key === "diamond";

  // Platinum gives up some plate to make room for its detached outer rim.
  const plateScale = isPlatinum ? 0.86 : 1;
  const plate = hex(plateScale);
  const field = hex(plateScale * (isPlatinum ? 0.76 : 0.78));
  const outerRim = hex(1);

  return (
    <Box
      component="svg"
      viewBox="0 0 96 108"
      {...(label
        ? { role: "img", "aria-label": label }
        : { "aria-hidden": "true", focusable: "false" })}
      sx={{
        width: size,
        height: "auto",
        display: "block",
        flexShrink: 0,
        overflow: "visible",
        // A static shadow, never animated.
        filter: dark
          ? "drop-shadow(0 5px 12px rgba(0,0,0,0.55))"
          : "drop-shadow(0 5px 12px rgba(15,23,42,0.20))",
        ...sx,
      }}
    >
      <defs>
        {/* Plate body: diagonal four-stop metal. */}
        <linearGradient id={id("plate")} x1="0.1" y1="0" x2="0.9" y2="1">
          <stop offset="0%" stopColor={m.hi} />
          <stop offset="34%" stopColor={m.sheen} />
          <stop offset="64%" stopColor={m.mid} />
          <stop offset="100%" stopColor={m.lo} />
        </linearGradient>

        {/* Rim bevel: lit from above, shadowed below. */}
        <linearGradient id={id("rim")} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={m.hi} />
          <stop offset="48%" stopColor={m.mid} />
          <stop offset="100%" stopColor={m.lo} />
        </linearGradient>

        {/* Recessed field: darker at the top edge, so it reads as inset. */}
        <linearGradient id={id("inset")} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={m.lo} />
          <stop offset="100%" stopColor={m.hi} />
        </linearGradient>

        {isPlatinum ? (
          <linearGradient id={id("field")} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={m.iri1} />
            <stop offset="45%" stopColor={m.iri2} />
            <stop offset="75%" stopColor={m.mid} />
            <stop offset="100%" stopColor={m.iri3} />
          </linearGradient>
        ) : (
          <radialGradient id={id("field")} cx="0.42" cy="0.36" r="0.8">
            <stop offset="0%" stopColor={m.sheen} />
            <stop offset="55%" stopColor={m.mid} />
            <stop offset="100%" stopColor={m.lo} />
          </radialGradient>
        )}

        {/* Specular sweep across the upper facets. */}
        <linearGradient id={id("gleam")} x1="0" y1="0" x2="0.8" y2="1">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.62" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
        </linearGradient>

        {/* Edge reflection along the top-left edges. */}
        <linearGradient id={id("edge")} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.9" />
          <stop offset="60%" stopColor="#FFFFFF" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Selected halo: theme-coloured, outside the metal. */}
      {selected && (
        <polygon
          points={pts(hex(1.09))}
          fill="none"
          stroke={theme.palette.primary.main}
          strokeOpacity="0.55"
          strokeWidth="2.4"
          strokeLinejoin="round"
        />
      )}

      {/* Platinum's detached outer rim: the double-rim silhouette. */}
      {isPlatinum && (
        <polygon
          points={pts(outerRim)}
          fill="none"
          stroke={url("rim")}
          strokeWidth="3.2"
          strokeLinejoin="round"
        />
      )}

      {/* Plate. Stroked with the rim gradient so corners round and bevel. */}
      <polygon
        points={pts(plate)}
        fill={url("plate")}
        stroke={url("rim")}
        strokeWidth={isPlatinum ? 5.5 : 7}
        strokeLinejoin="round"
      />

      {/* Recessed field. */}
      <polygon
        points={pts(field)}
        fill={url("field")}
        stroke={url("inset")}
        strokeOpacity="0.85"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />

      {/* Diamond: the field is cut into six planes, alternately lit. */}
      {isDiamond && (
        <g>
          {field.map((p, i) => {
            const q = field[(i + 1) % field.length];
            const tone = ["#FFFFFF", m.deep, "#FFFFFF", m.deep, "#FFFFFF", m.deep][i];
            const op = [0.34, 0.22, 0.16, 0.3, 0.1, 0.18][i];
            return (
              <polygon
                key={i}
                points={pts([[CX, CY], p, q])}
                fill={tone}
                fillOpacity={op}
              />
            );
          })}
        </g>
      )}

      {/* Silver: a fine inner ring inside the field. */}
      {key === "silver" && (
        <polygon
          points={pts(hex(0.64))}
          fill="none"
          stroke={m.hi}
          strokeOpacity="0.55"
          strokeWidth="1.1"
          strokeLinejoin="round"
        />
      )}

      {/* Gold: studs at the six corners of the rim. */}
      {key === "gold" && (
        <g>
          {hex(0.9).map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r="2.4" fill={m.hi} stroke={m.lo} strokeWidth="0.9" />
          ))}
        </g>
      )}

      {/* Specular sweep over the upper facets. */}
      <polygon
        points={pts([plate[5], plate[0], plate[1], [CX, CY - 4]])}
        fill={url("gleam")}
      />

      {/* ── Emblem, engraved: an ink shadow first, then the lit mark. ────── */}
      {isDiamond ? (
        <g strokeLinejoin="round" strokeLinecap="round">
          <g transform="translate(0 1.4)" fill="none" stroke={m.ink} strokeOpacity="0.4" strokeWidth="2.6">
            <path d={GEM_OUTLINE} />
          </g>
          <path d={GEM_OUTLINE} fill={m.hi} fillOpacity="0.28" stroke={m.hi} strokeWidth="2.4" />
          <g fill="none" stroke={m.hi} strokeWidth="1.4" strokeOpacity="0.9">
            {GEM_LINES.map((d) => <path key={d} d={d} />)}
          </g>
          {/* Sparkle. */}
          <path
            d="M66 30 L67.3 34.2 L71.5 35.5 L67.3 36.8 L66 41 L64.7 36.8 L60.5 35.5 L64.7 34.2 Z"
            fill="#FFFFFF"
            fillOpacity="0.95"
          />
        </g>
      ) : isPlatinum ? (
        <g strokeLinejoin="round">
          <path d={STAR} transform="translate(0 1.4)" fill={m.ink} fillOpacity="0.32" />
          <path d={STAR} fill={m.hi} stroke={m.lo} strokeOpacity="0.55" strokeWidth="0.9" />
          <circle cx={CX} cy={CY} r="2.6" fill={m.iri2} stroke={m.lo} strokeOpacity="0.5" strokeWidth="0.8" />
        </g>
      ) : (
        <g fill="none" strokeLinecap="round" strokeLinejoin="round">
          <g transform="translate(0 1.4)" stroke={m.ink} strokeOpacity="0.38" strokeWidth="5">
            {chevronPaths(CHEVRONS[key]).map((d) => <path key={d} d={d} />)}
          </g>
          <g stroke={m.hi} strokeOpacity="0.95" strokeWidth="4.6">
            {chevronPaths(CHEVRONS[key]).map((d) => <path key={d} d={d} />)}
          </g>
        </g>
      )}

      {/* Edge reflection along the top-left edges. */}
      <polyline
        points={pts([plate[4], plate[5], plate[0], plate[1]])}
        fill="none"
        stroke={url("edge")}
        strokeWidth="1.2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />

      {/* Hairline silhouette so the medallion holds its edge on a light page. */}
      <polygon
        points={pts(isPlatinum ? outerRim : plate)}
        fill="none"
        stroke={m.ink}
        strokeOpacity={dark ? 0.35 : 0.3}
        strokeWidth="1"
        strokeLinejoin="round"
      />
    </Box>
  );
}
